// The full working behind a late-coming hold / deduction, and the hours
// shortfall — shown as its own tab inside the Daily Attendance Breakdown.
//
// The daily table cannot carry this. Neither charge belongs to any single day:
// the late charge is settled once against the whole month's count, and the hours
// shortfall is a monthly total. Put as a footer they sat below the fold on a
// long month and were simply never seen, which is the same as not existing when
// somebody is asking why their pay is short.
//
// It shows the DATES, not just the count. "4 late days" invites an argument;
// "19, 20, 21 and 24 August, in at 10:15" ends one.

import { AlertTriangle, Clock } from 'lucide-react';
import type { DailyBreakdown, SalaryRecord } from '../api/salary';
import { formatHoursMinutes } from '../utils/hoursFormat';

const money = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const dayLabel = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

// Only the columns the table reads. The payslip passes its full DailyBreakdown;
// the Attendance card passes the live `late.days` rows, which carry just these.
export type LateDayLike = Pick<DailyBreakdown, 'date' | 'late' | 'clock_in' | 'hours'>;

interface Props {
  record: Pick<SalaryRecord, 'lateComing' | 'hoursShortfall'>;
  dailyData: LateDayLike[];
  // Mid-month, from the Attendance card: the month is not settled. The late
  // figures are what payroll would charge as things stand; the hours figure is
  // the worst case IF THE MONTH ENDED TODAY, and must not read as a deduction
  // already taken — the remaining days can still make it up.
  projected?: boolean;
}

export default function PayDeductionDetails({ record, dailyData, projected = false }: Props) {
  const late = record.lateComing;
  const hours = record.hoursShortfall;
  const lateDays = dailyData.filter((d) => d.late);

  const lateCharged = !!late && late.amount > 0;
  const hoursCharged = !!hours && hours.amount > 0;

  if (!lateCharged && !hoursCharged && lateDays.length === 0) {
    return (
      <div className="px-5 py-10 text-center">
        <p className="text-sm text-gray-500">
          Nothing was held or deducted this month.
        </p>
      </div>
    );
  }

  // Rounded: a live month can divide unevenly (₹1,550 / 3), and "₹516.667"
  // reads like a bug in a panel whose whole job is to be trusted.
  const perHalfDay =
    late && late.charged_days > 0 ? Math.round((late.amount / late.charged_days) * 100) / 100 : 0;

  return (
    <div className="px-5 py-4 space-y-5">
      {/* ---------------- late coming ---------------- */}
      {(lateCharged || lateDays.length > 0) && (
        <section>
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
            <Clock size={15} /> Late coming
          </h3>

          {lateDays.length > 0 && (
            <div className="mt-2 overflow-hidden rounded-lg border border-gray-200">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    <th className="px-3 py-1.5 text-left font-medium">Late on</th>
                    <th className="px-3 py-1.5 text-left font-medium">Clocked in</th>
                    <th className="px-3 py-1.5 text-right font-medium">Hours</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {lateDays.map((d) => (
                    <tr key={d.date}>
                      <td className="px-3 py-1.5 text-gray-800">{dayLabel(d.date)}</td>
                      <td className="px-3 py-1.5 font-mono text-gray-600">
                        {d.clock_in || '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-gray-600">
                        {formatHoursMinutes(d.hours)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {lateCharged ? (
            <div
              className={`mt-2 rounded-lg border p-3 text-xs ${
                late!.action === 'deduct'
                  ? 'border-red-200 bg-red-50 text-red-900'
                  : 'border-amber-200 bg-amber-50 text-amber-900'
              }`}
            >
              {/* Spell the arithmetic out. The charged count is not the same as
                  the number of days over the allowance, and that is exactly the
                  discrepancy somebody will query. */}
              <p>
                {late!.late_days} late {late!.late_days === 1 ? 'day' : 'days'} this
                month — {late!.charged_days} charged at half a day&apos;s pay
                {perHalfDay > 0 && ` (${money(perHalfDay)} each)`}
              </p>
              <p className="mt-1 text-sm font-semibold">
                {money(late!.amount)}{' '}
                {late!.action === 'deduct'
                  ? (projected ? 'TO BE DEDUCTED' : 'DEDUCTED')
                  : (projected ? 'GOING ON HOLD' : 'ON HOLD')}
              </p>
              <p className="mt-0.5">
                {late!.action === 'deduct'
                  ? (projected
                    ? 'Will be taken from this month’s salary as it stands — this does not come back.'
                    : 'Taken from this month’s salary — this does not come back.')
                  : 'Returned if next month stays within the free late days.'}
              </p>
              {late!.hold_balance > 0 && (
                <p className="mt-1.5 border-t border-current/20 pt-1.5">
                  Total held to date: <strong>{money(late!.hold_balance)}</strong>
                  {late!.released > 0 &&
                    ` · ${money(late!.released)} released this month`}
                </p>
              )}
            </div>
          ) : (
            <p className="mt-2 text-xs text-gray-500">
              Within the free late days — nothing charged.
            </p>
          )}
        </section>
      )}

      {/* ---------------- monthly hours ---------------- */}
      {hoursCharged && (
        <section>
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
            <AlertTriangle size={15} /> Monthly hours shortfall
          </h3>
          <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <p>
              {formatHoursMinutes(hours!.worked)} worked against a{' '}
              {formatHoursMinutes(hours!.required)} monthly minimum —{' '}
              {formatHoursMinutes(hours!.shortfall)} short{projected ? ' so far' : ''}.
            </p>
            <p className="mt-1 text-sm font-semibold">
              {money(hours!.amount)}{' '}
              {projected ? 'if the month ended today' : 'DEDUCTED'}
            </p>
            <p className="mt-0.5">
              {projected
                ? 'A projection, not a charge — the remaining days can still make the hours up.'
                : 'Charged at the hourly rate for the missing hours.'}
            </p>
          </div>
        </section>
      )}
    </div>
  );
}
